import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditPayment,
  setEditPayment,
  toggleCreateModal,
} from "../../features/payments/paymentsSlice";
import { SubmitHandler, useForm } from "react-hook-form";
import { IFormInput, OptionsProps } from "../../types/payments";
import { useParams } from "react-router-dom";
import moment from "moment";
import { t } from "../../locales";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2 } from "lucide-react";

type Props = {
  handleEditPayment: (data: IFormInput) => void;
};

const EditForm = ({ handleEditPayment }: Props) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const editPayment = useAppSelector(getEditPayment);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orderAmount, setOrderAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [statusOption] = useState<OptionsProps[]>([
    { value: 0, label: t.payments.statusOptions.basedOnReceived },
    { value: 1, label: t.payments.statusOptions.basedOnReturned },
  ]);

  const {
    handleSubmit,
    register,
    reset,
    getValues,
    formState: { errors },
  } = useForm<IFormInput>();

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (id) {
      setIsLoading(true);
      await handleEditPayment({ ...data, type: 0, id: editPayment?.id });
      setIsLoading(false);
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    reset();
    dispatch(toggleCreateModal(0));
    dispatch(setEditPayment(null));
  };

  useEffect(() => {
    if (editPayment && editPayment.id) {
      const total =
        parseFloat(`${editPayment.amount || 0}`) -
        parseFloat(`${editPayment.tax_amount || 0}`) -
        parseFloat(`${editPayment.roundoff_amount || 0}`);
      setOrderAmount(total);
      setTotalAmount(editPayment.amount || 0);

      reset({
        fromDate: editPayment.from_date,
        toDate: editPayment.to_date,
        tax_amount: editPayment.tax_amount,
        roundoff_amount: editPayment.roundoff_amount,
      });
    }
  }, [editPayment]);

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) handleCloseModal(); }}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-brand-dark">{t.payments.forms.editInvoiceTitle}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Readonly fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label>{t.payments.formFields.status}</Label>
              <Input
                value={statusOption.find((val) => val.value === editPayment?.amount_status)?.label || ""}
                readOnly
                className="bg-gray-100"
              />
            </div>
            <div>
              <Label>{t.payments.formFields.fromDate}</Label>
              <Input
                value={moment(editPayment?.from_date).format("DD-MM-YYYY")}
                readOnly
                className="bg-gray-100"
              />
            </div>
            <div>
              <Label>{t.payments.formFields.toDate}</Label>
              <Input
                value={moment(editPayment?.to_date).format("DD-MM-YYYY")}
                readOnly
                className="bg-gray-100"
              />
            </div>
          </div>

          {/* Editable fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label>{t.payments.formFields.paymentAmount}</Label>
              <Input value={orderAmount || 0} readOnly className="bg-gray-100" />
            </div>
            <div>
              <Label>{t.payments.formFields.taxAmount}</Label>
              <Input
                type="number"
                step="any"
                placeholder={t.payments.formFields.taxAmount}
                {...register("tax_amount", { required: false })}
                onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
                onKeyUp={(e: any) => {
                  const { roundoff_amount } = getValues();
                  const tax_amount = e.target.value || 0;
                  setTotalAmount(
                    parseFloat(`${orderAmount || 0}`) +
                    parseFloat(`${tax_amount || 0}`) +
                    parseFloat(`${roundoff_amount || 0}`)
                  );
                }}
              />
            </div>
            <div>
              <Label>{t.payments.formFields.roundOff}</Label>
              <Input
                type="number"
                step="any"
                placeholder={t.payments.formFields.roundOff}
                {...register("roundoff_amount", { required: false })}
                onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
                onKeyUp={(e: any) => {
                  const { tax_amount } = getValues();
                  const roundoff_amount = e.target.value || 0;
                  setTotalAmount(
                    parseFloat(`${orderAmount || 0}`) +
                    parseFloat(`${tax_amount || 0}`) +
                    parseFloat(`${roundoff_amount || 0}`)
                  );
                }}
              />
            </div>
          </div>

          <p className="text-xl font-bold text-brand-dark mb-4">
            {t.payments.formFields.totalAmount}: ₹{totalAmount ? totalAmount.toFixed(2) : "0.00"}
          </p>

          <div className="fixed bottom-0 left-0 right-0 flex justify-center gap-3 p-4 border-t bg-white z-10 sm:static sm:p-0 sm:z-auto sm:border-t-0 sm:bg-transparent">
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-[#113F67] hover:bg-[#1a5a8a] text-white"
            >
              {isLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t.payments.buttons.saving}</> : t.payments.buttons.continue}
            </Button>
            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={isLoading}>
              {t.payments.buttons.close}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditForm;
